import { AutoHeight } from "@asup/context-menu";
import { UniqueIdentifier } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties } from "react";
import { GripVertical } from "react-bootstrap-icons";
import styles from "./SortableList.module.css";

interface SortableListItemProps {
  id: UniqueIdentifier;
  canMove?: boolean;
  children: React.ReactElement;
  maxHeight?: number;
  showBorder?: boolean;
}

/**
 * Drag handle button for sortable list items.
 *
 * @param props - The component props
 * @param props.listeners - Event listeners for drag interactions
 * @param props.maxHeight - Optional maximum height for the grip container
 */
const ButtonGrip = ({
  listeners,
  maxHeight,
}: {
  listeners?: SyntheticListenerMap;
  maxHeight?: number;
}): React.ReactElement => (
  <div
    className={styles.sortableItemGripHolder}
    {...listeners}
    role={"button"}
    style={{
      maxHeight: maxHeight !== undefined ? `${maxHeight}px` : "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <GripVertical />
  </div>
);
ButtonGrip.displayName = "ButtonGrip";

/**
 * Individual sortable list item with optional drag handle.
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the sortable item
 * @param props.canMove - Optional. When true, displays drag handle
 * @param props.children - Child element to display within the item
 * @param props.maxHeight - Optional maximum height for the item
 * @param props.showBorder - Optional. When true, displays border around the item (default: true)
 */
export function SortableListItem({
  id,
  canMove,
  children,
  maxHeight,
  showBorder = true,
}: SortableListItemProps): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      role={"tab"}
      style={style}
    >
      <AutoHeight hide={maxHeight === 0}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {canMove && (
            <ButtonGrip
              listeners={listeners}
              maxHeight={maxHeight}
            />
          )}
          <div className={styles.sortableItemAfterGrip}>
            <div className={showBorder ? `m-1 ${styles.sortableItemBorder}` : "m-1"}>
              {children}
            </div>
          </div>
        </div>
      </AutoHeight>
    </div>
  );
}

SortableListItem.displayName = "SortableListItem";
